/**
 * The prototype's Settings card parts (TMMAPP-0103, audit A4 §8, P:1743-1800): a card, padding 20,
 * gap 18, sections split by a top `line` (padding-top 16), each a 15/500 title with a 12px `text2`
 * line. TMMAPP-0119 item 23 puts them in tabs: Appearance (Light / Dark / System as text) is its own
 * tab; Language and Operating currency (TMMAPI-0143) sit in Profile; the Subscription summary — Plan
 * (the tier pill), Billing interval, Status, Next charge, then "Manage subscription", which discloses
 * the management (renewal, the interval, "active until", Cancel subscription) — heads the
 * Subscription tab and reads the page's one billing profile (TMMAPP-0112).
 */
import { useState, type ReactNode } from 'react';
import {
  Alert,
  Box,
  Button,
  Chip,
  Collapse,
  FormControl,
  InputLabel,
  MenuItem,
  Paper,
  Select,
  Stack,
  Typography,
  useTheme,
} from '@mui/material';
import { useTranslation } from 'react-i18next';
import { ErrorCodes, isApiError } from '@/api/errors';
import { patchMe } from '@/api/settings';
import { CollapseIcon, ExpandIcon } from '@/components/icons';
import { SegmentedControl } from '@/components/SegmentedControl';
import { statusLabel } from '@/components/statusLabel';
import { useThemeMode, type ThemeModePreference } from '@/context/theme-mode-context';
import { SUBSCRIBE_CURRENCIES } from '@/features/subscribe/currencies';
import { DEFAULT_LOCALE, normalizeLocale, SUPPORTED_LOCALES } from '@/i18n/constants';
import { currencyDisplayName, formatCivilDate } from '@/i18n/format';
import { useLocale } from '@/i18n/useLocale';
import { SellerLine } from '@/features/billing/SellerLine';
import { useMe, useMeRefetch } from '@/session/meHooks';
import { resolveTokens } from '@/theme/theme';
import { useBillingProfile } from './billingProfileContext';
import type { SettingsNotify } from './settingsHref';
import { SubscriptionTab } from './SubscriptionTab';

/** The card that holds a tab's sections. */
export function SettingsCard({ children }: { children: ReactNode }) {
  return (
    <Paper sx={{ p: '20px', display: 'flex', flexDirection: 'column', gap: '18px' }}>
      {children}
    </Paper>
  );
}

/** One section of a card: a top rule unless it is the first. */
export function SettingsSection({
  id,
  title,
  description,
  first = false,
  children,
}: {
  /** The heading's id: the section is labelled by it. */
  id: string;
  title: string;
  description?: string | undefined;
  first?: boolean;
  children: ReactNode;
}) {
  const tokens = resolveTokens(useTheme());
  return (
    <Box
      component="section"
      aria-labelledby={id}
      sx={first ? undefined : { borderTop: `1px solid ${tokens.line}`, pt: '16px' }}
    >
      <Typography
        variant="h2"
        id={id}
        sx={{
          fontSize: 15,
          lineHeight: '22px',
          fontWeight: 500,
          letterSpacing: 0,
          mb: description === undefined ? '10px' : 0,
        }}
      >
        {title}
      </Typography>
      {description !== undefined && (
        <Typography sx={{ fontSize: 12, lineHeight: '18px', color: tokens.text2, mb: '10px' }}>
          {description}
        </Typography>
      )}
      {children}
    </Box>
  );
}

function Row({ label, value }: { label: string; value: ReactNode }) {
  const tokens = resolveTokens(useTheme());
  return (
    <Box sx={{ display: 'flex', justifyContent: 'space-between', gap: '12px', fontSize: 13 }}>
      <Box component="span" sx={{ color: tokens.text2 }}>
        {label}
      </Box>
      <Box component="span" sx={{ fontWeight: 500, textAlign: 'end' }}>
        {value}
      </Box>
    </Box>
  );
}

export function AppearanceSection() {
  const { t } = useTranslation();
  const { themeMode, setThemeMode } = useThemeMode();
  return (
    <SettingsSection
      first
      id="settings-appearance"
      title={t('settings.appearance')}
      description={t('settings.appearanceHelp')}
    >
      <SegmentedControl<ThemeModePreference>
        aria-label={t('theme.label')}
        value={themeMode}
        wide
        options={[
          { value: 'light', label: t('theme.light') },
          { value: 'dark', label: t('theme.dark') },
          { value: 'system', label: t('theme.system') },
        ]}
        onChange={setThemeMode}
      />
    </SettingsSection>
  );
}

export function LanguageSection({ onNotify }: { onNotify: SettingsNotify }) {
  const { t, i18n } = useTranslation();
  const refetchMe = useMeRefetch();
  const [languageBusy, setLanguageBusy] = useState(false);
  const currentLocale = normalizeLocale(i18n.language) ?? DEFAULT_LOCALE;

  async function changeLanguage(code: string) {
    if (languageBusy) return;
    const next = normalizeLocale(code) ?? DEFAULT_LOCALE;
    setLanguageBusy(true);
    await i18n.changeLanguage(next);
    try {
      await patchMe({ locale: next });
      await refetchMe();
    } catch {
      // The browser keeps the language (`tmm_locale`); only the saved preference didn't update.
      onNotify(t('settings.errorGeneric'), 'error');
    } finally {
      setLanguageBusy(false);
    }
  }

  return (
    <SettingsSection
      id="settings-language"
      title={t('language.label')}
      description={t('settings.languageHelp')}
    >
      <FormControl size="small" sx={{ minWidth: 260 }}>
        <InputLabel
          id="settings-language-label"
          sx={{ position: 'absolute', clipPath: 'inset(50%)' }}
        >
          {t('language.label')}
        </InputLabel>
        <Select
          labelId="settings-language-label"
          value={currentLocale}
          disabled={languageBusy}
          onChange={(event) => void changeLanguage(String(event.target.value))}
        >
          {SUPPORTED_LOCALES.map((code) => (
            <MenuItem key={code} value={code} lang={code}>
              {t(`language.${code}`)}
            </MenuItem>
          ))}
        </Select>
      </FormControl>
    </SettingsSection>
  );
}

export function CurrencySection({ onNotify }: { onNotify: SettingsNotify }) {
  const { t } = useTranslation();
  const locale = useLocale();
  const tokens = resolveTokens(useTheme());
  const me = useMe();
  const refetchMe = useMeRefetch();
  const [currencyBusy, setCurrencyBusy] = useState(false);
  const [currencyError, setCurrencyError] = useState<string | null>(null);

  const currency = me.user.currency;
  // Not editable unless the API says it is, and never without a current value to show.
  const locked = me.currencyLocked !== false || currency === null;
  const currencies =
    currency !== null && !SUBSCRIBE_CURRENCIES.includes(currency)
      ? [currency, ...SUBSCRIBE_CURRENCIES]
      : SUBSCRIBE_CURRENCIES;

  async function changeCurrency(code: string) {
    if (code === currency || currencyBusy) return;
    setCurrencyBusy(true);
    setCurrencyError(null);
    try {
      await patchMe({ currency: code });
      await refetchMe();
      onNotify(t('settings.saved'));
    } catch (error) {
      if (isApiError(error) && error.code === ErrorCodes.CURRENCY_LOCKED) {
        // Something is denominated in it now: show the locked state, never a half change.
        setCurrencyError(t('settings.currencyLockedError'));
        await refetchMe();
      } else {
        setCurrencyError(t('settings.errorGeneric'));
      }
    } finally {
      setCurrencyBusy(false);
    }
  }

  return (
    <SettingsSection
      id="settings-currency"
      title={t('settings.operatingCurrency')}
      description={t('settings.currencyHelp')}
    >
      {locked ? (
        <Typography sx={{ fontSize: 14, fontWeight: 500 }} data-currency-locked="">
          {currency === null
            ? t('settings.currencyNotSet')
            : t('settings.currencyOption', {
                code: currency,
                name: currencyDisplayName(currency, locale),
              })}
        </Typography>
      ) : (
        <FormControl size="small" sx={{ minWidth: 260 }}>
          <InputLabel
            id="settings-currency-label"
            sx={{ position: 'absolute', clipPath: 'inset(50%)' }}
          >
            {t('settings.operatingCurrency')}
          </InputLabel>
          <Select
            labelId="settings-currency-label"
            value={currency ?? ''}
            disabled={currencyBusy}
            onChange={(event) => void changeCurrency(String(event.target.value))}
          >
            {currencies.map((code) => (
              <MenuItem key={code} value={code}>
                {t('settings.currencyOption', {
                  code,
                  name: currencyDisplayName(code, locale),
                })}
              </MenuItem>
            ))}
          </Select>
        </FormControl>
      )}
      {locked && currency !== null && (
        <Typography sx={{ fontSize: 12, color: tokens.text2, mt: 1 }}>
          {t('settings.currencyLocked')}
        </Typography>
      )}
      {currencyError !== null && (
        <Alert severity="warning" sx={{ mt: 1 }}>
          {currencyError}
        </Alert>
      )}
    </SettingsSection>
  );
}

interface SubscriptionSummaryProps {
  onNotify: SettingsNotify;
  /** The management starts disclosed (a locked person's Renew lives there). */
  manageOpen?: boolean;
}

export function SubscriptionSummary({ onNotify, manageOpen = false }: SubscriptionSummaryProps) {
  const { t } = useTranslation();
  const locale = useLocale();
  const me = useMe();
  const { profile: billing } = useBillingProfile();
  const [managing, setManaging] = useState(manageOpen);

  const subscriber = me.tier === 'SUBSCRIBER' && !me.locked;
  // The rows read "—" until the profile is in, or when there is none to read.
  const subscription = subscriber ? (billing?.subscription ?? null) : null;
  const dash = '—';
  const nextCharge = subscription?.nextBillingOn
    ? safeCivil(subscription.nextBillingOn, locale)
    : dash;

  return (
    <SettingsSection
      first
      id="settings-subscription-summary"
      title={t('settings.tabSubscription')}
      description={t('settings.subscriptionHelp')}
    >
      <Stack sx={{ gap: 1 }}>
        <Row
          label={t('settings.planLabel')}
          value={<Chip label={t(`tier.${me.tier}`)} size="small" data-tier-pill="" />}
        />
        <Row
          label={t('settings.intervalLabel')}
          value={subscription?.interval ? t(`intervalBilling.term.${subscription.interval}`) : dash}
        />
        <Row
          label={t('settings.statusLabel')}
          value={subscription ? statusLabel(t, 'subscription', subscription.status) : dash}
        />
        <Row label={t('settings.nextChargeLabel')} value={nextCharge} />
        {/* Who bills the person, beside the plan (TMMAPP-0119 §A3); omitted when not served. */}
        <SellerLine />
      </Stack>
      <Box sx={{ mt: 1.5 }}>
        <Button
          size="small"
          aria-expanded={managing}
          aria-controls="settings-manage-subscription"
          endIcon={managing ? <CollapseIcon /> : <ExpandIcon />}
          onClick={() => setManaging((open) => !open)}
          sx={{ marginInlineStart: -1 }}
        >
          {t('settings.manageSubscription')}
        </Button>
      </Box>
      <Collapse in={managing} unmountOnExit>
        <Box id="settings-manage-subscription" sx={{ pt: 1.5 }}>
          <SubscriptionTab onNotify={onNotify} />
        </Box>
      </Collapse>
    </SettingsSection>
  );
}

/** A civil date as received; one that isn't shape-valid reads as given. */
function safeCivil(civil: string, locale: string): string {
  try {
    return formatCivilDate(civil, locale);
  } catch {
    return civil;
  }
}
